import { isNil as _isNil } from 'lodash';
import * as dayjs from 'dayjs';
import { DriverActivity, GoogleTravelMode } from '../types/shared-location.types';

export type CalculateEtaOptions = {
	origin: google.maps.LatLngLiteral;
	destination: google.maps.LatLngLiteral;
	destinationAddress: string;
	travelMode?: GoogleTravelMode;
};

class EtaService {
	public async calculateEta(
		calculateEtaOptions: CalculateEtaOptions
	): Promise<{ duration: string; distance: string }> {
		const { rows } = await this.calculateEtaFromGoogle(calculateEtaOptions);
		const [result] = rows;
		const { elements } = result;
		const [eta] = elements;
		const { status, duration, distance } = eta;

		if (status != google.maps.DistanceMatrixElementStatus.OK) {
			throw new Error('EtaService calculateEta empty results');
		}

		return { duration: dayjs().add(duration.value, 'second').toISOString(), distance: distance.text };
	}

	public convertDriverActivityToTravelMode = (driverActivity: DriverActivity): GoogleTravelMode => {
		switch (driverActivity) {
			case DriverActivity.NotMoving:
				return GoogleTravelMode.DRIVING;
			case DriverActivity.Walking:
			case DriverActivity.Running:
				return GoogleTravelMode.WALKING;
			case DriverActivity.Bicycle:
				return GoogleTravelMode.BICYCLING;
			case DriverActivity.Unknown:
			case DriverActivity.Driving:
				return GoogleTravelMode.DRIVING;
			case DriverActivity.PublicTransport:
				return GoogleTravelMode.TRANSIT;

			default:
				return GoogleTravelMode.DRIVING;
		}
	};

	public static isEtaPassed = (eta: string) => Boolean(EtaService.getHowLongFromNow(eta) < 0);

	public static getHowLongFromNow = (date: Date | string) => {
		return dayjs(date).diff(dayjs(), 'minute');
	};

	private async calculateEtaFromGoogle({
		origin,
		destination,
		destinationAddress,
		travelMode = GoogleTravelMode.DRIVING
	}: CalculateEtaOptions): Promise<google.maps.DistanceMatrixResponse> {
		return new Promise((resolve, reject) => {
			if (_isNil(window.google)) {
				reject('EtaService calculateEtaFromGoogle google maps not initialized yet');
			}

			if (!this.isValidPosition(origin)) {
				reject('EtaService calculateEtaFromGoogle can not calculate eta, driver location is not available');
			}

			const distanceMatrixService = new google.maps.DistanceMatrixService();
			const origins = [origin];

			const destinations = [this.isValidPosition(destination) ? destination : destinationAddress] as
				| string[]
				| google.maps.LatLngLiteral[];

			distanceMatrixService.getDistanceMatrix(
				{
					origins,
					destinations,
					travelMode: travelMode as unknown as google.maps.TravelMode,
					unitSystem: google.maps.UnitSystem.METRIC
				},
				(response, status) => {
					if (status === google.maps.DistanceMatrixStatus.OK) {
						resolve(response);
					} else {
						reject('Failed to calculate eta from google');
					}
				}
			);
		});
	}

	private isValidPosition({ lat, lng }: google.maps.LatLngLiteral) {
		return Boolean(lat && lng);
	}
}

export default EtaService;
